import { useState, useEffect, useRef } from 'react'
import { Clock, Sun, Sunset } from 'lucide-react'

interface TimePickerProps {
  value?: string | null
  onChange: (time: string) => void
  onClose: () => void
}

// Generate time slots from 9am to 8pm in 15-minute intervals
const generateTimeSlots = () => {
  const slots: { time: string; display: string; period: 'morning' | 'afternoon' | 'evening' }[] = []
  
  for (let hour = 9; hour <= 20; hour++) {
    for (let min = 0; min < 60; min += 15) {
      if (hour === 20 && min > 0) break // Stop at 8pm
      
      const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`
      const displayHour = hour > 12 ? hour - 12 : hour
      const ampm = hour >= 12 ? 'pm' : 'am'
      const display = `${displayHour}:${min.toString().padStart(2, '0')}${ampm}`
      
      let period: 'morning' | 'afternoon' | 'evening' = 'morning'
      if (hour >= 12 && hour < 17) period = 'afternoon'
      else if (hour >= 17) period = 'evening'
      
      slots.push({ time, display, period })
    }
  }
  
  return slots
}

const TIME_SLOTS = generateTimeSlots()

export function TimePicker({ value, onChange, onClose }: TimePickerProps) {
  const [filter, setFilter] = useState<'all' | 'morning' | 'afternoon' | 'evening'>('all')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  const filteredSlots = filter === 'all' 
    ? TIME_SLOTS 
    : TIME_SLOTS.filter(s => s.period === filter)

  const selectTime = (time: string) => {
    onChange(time)
    onClose()
  }

  return (
    <div 
      ref={ref}
      className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 w-72"
    >
      {/* Period Filter - 2 rows for better fit */}
      <div className="grid grid-cols-4 gap-1 mb-3 p-1 bg-gray-100 rounded-lg">
        <button
          onClick={() => setFilter('all')}
          className={`py-1.5 text-[11px] font-medium rounded transition-colors ${
            filter === 'all' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFilter('morning')}
          className={`py-1.5 text-[11px] font-medium rounded transition-colors flex items-center justify-center gap-0.5 ${
            filter === 'morning' ? 'bg-white shadow text-amber-600' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Sun className="w-3 h-3" />
          AM
        </button>
        <button
          onClick={() => setFilter('afternoon')}
          className={`py-1.5 text-[11px] font-medium rounded transition-colors flex items-center justify-center gap-0.5 ${
            filter === 'afternoon' ? 'bg-white shadow text-orange-600' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Clock className="w-3 h-3" />
          PM
        </button>
        <button
          onClick={() => setFilter('evening')}
          className={`py-1.5 text-[11px] font-medium rounded transition-colors flex items-center justify-center gap-0.5 ${
            filter === 'evening' ? 'bg-white shadow text-purple-600' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Sunset className="w-3 h-3" />
          Eve
        </button>
      </div>

      {/* Time Grid */}
      <div className="grid grid-cols-4 gap-1.5 max-h-48 overflow-y-auto">
        {filteredSlots.map((slot) => (
          <button
            key={slot.time}
            onClick={() => selectTime(slot.time)}
            className={`
              py-2 px-1 text-sm rounded-lg font-medium transition-all
              ${value === slot.time 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-50 hover:bg-blue-100 text-gray-700'}
              ${slot.period === 'morning' && value !== slot.time ? 'hover:bg-amber-50' : ''}
              ${slot.period === 'afternoon' && value !== slot.time ? 'hover:bg-orange-50' : ''}
              ${slot.period === 'evening' && value !== slot.time ? 'hover:bg-purple-50' : ''}
            `}
          >
            {slot.display}
          </button>
        ))}
      </div>

      {/* Current Selection */}
      {value && (
        <div className="mt-3 pt-3 border-t text-center">
          <span className="text-sm text-gray-600">Selected: </span>
          <span className="text-sm font-semibold text-blue-600">
            {TIME_SLOTS.find(s => s.time === value)?.display || value}
          </span>
        </div>
      )}
    </div>
  )
}
